import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  X, Search, ArrowRight, Cpu, LayoutGrid, GraduationCap, TrendingUp, Navigation,
  Cloud, Droplets, Scan, Map, CalendarRange, BookOpen, MessageCircle, Target,
  Sprout, DollarSign, Trophy, Rocket, Users, Settings, ChevronLeft, Lightbulb,
  CheckCircle2, ShoppingBasket,
} from 'lucide-react';

import { useTour } from '@/welcome/TourContext';

const C = { forest: '#1A4331', deep: '#0F2D1F', lime: '#B4D330', leaf: '#6B9F4A', cream: '#FAF6EC', gold: '#C9A85B', muted: '#5C665C' };

// Feature guides grouped by hub. `target` is a CSS selector on the destination page
// to spotlight (the page's main heading, or the specific control).
export const WEB_GUIDES = [
  {
    hub: 'Planning', Icon: LayoutGrid, items: [
      { id: 'planner', title: 'Garden Planner', Icon: Map, route: '/planner', target: 'main h1', desc: 'Lay out your beds and drag crops into place. Plan spacing, companions and rotation visually, bed by bed.' },
      { id: 'what-grow', title: 'What Can I Grow?', Icon: Sprout, route: '/planner', target: '[data-testid="what-can-i-grow-pill"]', desc: 'See every crop you can sow, plant or harvest this month in your region. Tick each one to plan and track your season.' },
      { id: 'season', title: 'Season Plan', Icon: CalendarRange, route: '/season-plan', target: 'main h1', desc: 'Map your whole growing year at a glance. See what is coming up and never miss a sowing window.' },
    ],
  },
  {
    hub: 'Intelligence', Icon: Cpu, items: [
      { id: 'weather', title: 'Weather', Icon: Cloud, route: '/weather', target: 'main h1', desc: 'Your garden-specific forecast — frost, heat and rain. Know exactly when to protect, water or sow.' },
      { id: 'watering', title: 'Watering', Icon: Droplets, route: '/watering', target: 'main h1', desc: 'A daily watering guide tuned to your crops and the weather. Water the right amount, never too much or too little.' },
      { id: 'scanner', title: 'Plant Scanner', Icon: Scan, route: '/scanner', target: 'main h1', desc: 'Snap a photo of a sick leaf and get an instant diagnosis. Find out what is wrong and how to fix it.' },
    ],
  },
  {
    hub: 'Learning', Icon: GraduationCap, items: [
      { id: 'handbook', title: 'Crop Handbook', Icon: BookOpen, route: '/crops', target: 'main h1', desc: 'A plain-English guide to every crop — how to sow, feed, and harvest it. Your go-to reference whenever you are unsure.' },
      { id: 'coach', title: 'Garden Coach', Icon: MessageCircle, route: '/coach', target: 'main h1', desc: 'Ask anything about your garden and get friendly, expert answers. Like having a seasoned gardener on call.' },
      { id: 'skills', title: 'Your Skills', Icon: Target, route: '/skills', target: 'main h1', desc: 'Grow your know-how as you garden and unlock new badges. See how far you have come and what to learn next.' },
    ],
  },
  {
    hub: 'Results', Icon: TrendingUp, items: [
      { id: 'harvests', title: 'Harvests', Icon: TrendingUp, route: '/harvests', target: 'main h1', desc: 'Log what you pick and see when each crop will be ready. Watch your harvests add up through the season.' },
      { id: 'value', title: 'Garden Value', Icon: DollarSign, route: '/value', target: 'main h1', desc: 'See how much money your garden has saved you at the shops. Turn your effort into a number you can be proud of.' },
      { id: 'progress', title: 'Progress', Icon: Trophy, route: '/progress', target: 'main h1', desc: 'Track your streaks, milestones and wins over time. A little nudge to keep your garden thriving.' },
    ],
  },
];

// Maps an article's logical `spotlight` key → a web route + element to spotlight.
// null = no in-app destination on web (hide the "Show me" button).
const SPOTLIGHT_WEB = {
  home: { route: '/today', target: 'main h1' },
  calendar: { route: '/calendar', target: '[data-testid="planting-calendar"]' },
  season: { route: '/season-plan', target: 'main h1' },
  planner: { route: '/planner', target: 'main h1' },
  today: { route: '/today', target: 'main h1' },
  'my-crops': { route: '/my-crops', target: 'main h1' },
  indoor: null,
  weather: { route: '/weather', target: '[data-testid="current-weather"]' },
  watering: { route: '/watering', target: 'main h1' },
  harvests: { route: '/harvests', target: 'main h1' },
  coach: { route: '/coach', target: 'main h1' },
  scanner: { route: '/scanner', target: 'main h1' },
  community: { route: '/community', target: 'main h1' },
  exchange: { route: '/community', target: 'main h1' },
  value: { route: '/value', target: 'main h1' },
  progress: { route: '/progress', target: 'main h1' },
  settings: { route: '/settings', target: 'main h1' },
};
const KB_CAT_ICON = {
  'getting-started': Rocket, planning: CalendarRange, 'growing-care': Sprout,
  'weather-intel': Cloud, 'ai-coach': Cpu, 'community-exchange': Users,
  'harvests-results': ShoppingBasket, 'account-billing': Settings,
};

export function HelpCentre() {
  const { closeHelp, showMe, start } = useTour();
  const [tab, setTab] = useState('articles');
  const [q, setQ] = useState('');
  const [kb, setKb] = useState({ categories: [], articles: [] });
  const [openId, setOpenId] = useState(null);
  const query = q.trim().toLowerCase();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/help`)
      .then((r) => r.json())
      .then((d) => setKb({ categories: d.categories || [], articles: d.articles || [] }))
      .catch(() => {});
  }, []);

  // --- Guides (existing feature tour) ---
  const sections = WEB_GUIDES.map((s) => ({
    ...s,
    items: s.items.filter((it) => !query || it.title.toLowerCase().includes(query) || it.desc.toLowerCase().includes(query) || s.hub.toLowerCase().includes(query)),
  })).filter((s) => s.items.length);

  // --- Knowledge Base ---
  const kbMatch = (a) => !query || a.title.toLowerCase().includes(query) || (a.summary || '').toLowerCase().includes(query) || (a.keywords || []).some((k) => k.includes(query)) || (a.steps || []).some((s) => s.toLowerCase().includes(query));
  const kbArticles = kb.articles.filter(kbMatch);
  const openArticle = kb.articles.find((a) => a.id === openId) || null;
  const spotFor = (a) => a && SPOTLIGHT_WEB[a.spotlight];
  const showArticle = (a) => {
    const sp = spotFor(a);
    if (!sp) return;
    closeHelp();
    showMe({ id: a.id, title: a.title, desc: a.summary, route: sp.route, target: sp.target });
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center" style={{ zIndex: 1100, background: 'rgba(15,45,31,0.55)', backdropFilter: 'blur(6px)' }} data-testid="help-centre">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ type: 'spring', damping: 22 }}
        className="hg-glass w-full max-w-2xl mt-10 mb-6 mx-4 rounded-3xl overflow-hidden flex flex-col" style={{ maxHeight: '88vh' }}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div>
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>Help Centre</h2>
            <p className="text-sm" style={{ color: C.muted }}>Search how-to articles or take a guided tour.</p>
          </div>
          <button onClick={closeHelp} data-testid="help-close" className="hg-press w-10 h-10 rounded-full bg-white/70 hover:bg-white flex items-center justify-center">
            <X className="w-5 h-5" style={{ color: C.muted }} />
          </button>
        </div>

        {!openArticle && (
          <div className="px-6 pb-3 flex gap-2">
            <button onClick={() => setTab('articles')} data-testid="help-tab-articles"
              className="flex-1 rounded-full py-2 text-sm font-bold" style={tab === 'articles' ? { background: C.forest, color: C.cream } : { background: 'rgba(26,67,49,0.08)', color: C.forest }}>Knowledge Base</button>
            <button onClick={() => setTab('guides')} data-testid="help-tab-guides"
              className="flex-1 rounded-full py-2 text-sm font-bold" style={tab === 'guides' ? { background: C.forest, color: C.cream } : { background: 'rgba(26,67,49,0.08)', color: C.forest }}>Feature Guides</button>
          </div>
        )}

        {!openArticle && (
          <div className="px-6 pb-3">
            <div className="flex items-center gap-2 rounded-2xl px-4 py-3" style={{ background: '#fff', border: '1px solid rgba(26,67,49,0.1)' }}>
              <Search className="w-[18px] h-[18px]" style={{ color: C.muted }} />
              <input data-testid="help-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={tab === 'articles' ? 'Search help articles…' : 'Search for a feature…'}
                className="flex-1 bg-transparent outline-none text-[15px]" style={{ color: C.forest }} />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto hg-scroll px-6 pb-4 space-y-5">
          {/* ---------- Article detail ---------- */}
          {openArticle ? (
            <div data-testid="help-article-detail">
              <button onClick={() => setOpenId(null)} data-testid="help-article-back" className="hg-press inline-flex items-center gap-1.5 text-sm font-semibold mb-3" style={{ color: C.muted }}>
                <ChevronLeft className="w-4 h-4" /> Back to articles
              </button>
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{openArticle.title}</h3>
              <p className="text-[14px] mt-1.5" style={{ color: C.muted, lineHeight: 1.5 }}>{openArticle.summary}</p>
              {openArticle.steps?.length > 0 && (
                <div className="mt-4 space-y-2.5">
                  {openArticle.steps.map((s, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold" style={{ background: 'rgba(107,159,74,0.18)', color: C.forest }}>{i + 1}</span>
                      <span className="text-[14px]" style={{ color: C.forest, lineHeight: 1.45 }}>{s}</span>
                    </div>
                  ))}
                </div>
              )}
              {openArticle.tips?.length > 0 && (
                <div className="mt-4 rounded-2xl p-4" style={{ background: 'rgba(201,168,91,0.14)' }}>
                  {openArticle.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1.5 last:mb-0">
                      <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: C.gold }} />
                      <span className="text-[13px]" style={{ color: C.forest, lineHeight: 1.45 }}>{tip}</span>
                    </div>
                  ))}
                </div>
              )}
              {spotFor(openArticle) && (
                <button onClick={() => showArticle(openArticle)} data-testid={`help-article-showme-${openArticle.id}`}
                  className="hg-press mt-5 w-full inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-[14px] font-bold" style={{ background: C.forest, color: C.cream }}>
                  Show me in the app <ArrowRight className="w-4 h-4" style={{ color: C.lime }} />
                </button>
              )}
            </div>
          ) : tab === 'articles' ? (
            /* ---------- Knowledge Base list ---------- */
            <>
              {kb.categories.map((cat) => {
                const arts = kbArticles.filter((a) => a.category === cat.id);
                if (!arts.length) return null;
                const CatIcon = KB_CAT_ICON[cat.id] || BookOpen;
                return (
                  <div key={cat.id} data-testid={`help-kb-cat-${cat.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <CatIcon className="w-[18px] h-[18px]" style={{ color: C.leaf }} />
                      <p className="text-[12px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{cat.title}</p>
                    </div>
                    <div className="space-y-2">
                      {arts.map((a) => (
                        <button key={a.id} onClick={() => setOpenId(a.id)} data-testid={`help-article-${a.id}`}
                          className="hg-press w-full text-left rounded-2xl p-4 flex items-center gap-3" style={{ background: 'rgba(255,255,255,0.65)' }}>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[15px]" style={{ color: C.forest }}>{a.title}</p>
                            <p className="text-[13px] mt-0.5 truncate" style={{ color: C.muted }}>{a.summary}</p>
                          </div>
                          <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: C.leaf }} />
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {kbArticles.length === 0 && <p className="text-center text-sm py-8" style={{ color: C.muted }}>No articles match “{q}”.</p>}
            </>
          ) : (
            /* ---------- Feature guides (existing) ---------- */
            <>
              {sections.map((s) => (
                <div key={s.hub}>
                  <div className="flex items-center gap-2 mb-2">
                    <s.Icon className="w-[18px] h-[18px]" style={{ color: C.leaf }} />
                    <p className="text-[12px] font-bold uppercase tracking-wider" style={{ color: C.muted }}>{s.hub}</p>
                  </div>
                  <div className="space-y-2">
                    {s.items.map((it) => (
                      <div key={it.id} data-testid={`help-guide-${it.id}`} className="rounded-2xl p-4 flex items-start gap-3" style={{ background: 'rgba(255,255,255,0.65)' }}>
                        <span className="flex items-center justify-center rounded-xl flex-shrink-0" style={{ width: 40, height: 40, background: 'rgba(26,67,49,0.08)' }}>
                          <it.Icon className="w-5 h-5" style={{ color: C.forest }} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-[15px]" style={{ color: C.forest }}>{it.title}</p>
                          <p className="text-[13px] mt-0.5" style={{ color: C.muted, lineHeight: 1.45 }}>{it.desc}</p>
                          <button onClick={() => showMe(it)} data-testid={`help-showme-${it.id}`}
                            className="hg-press mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[12px] font-semibold" style={{ background: C.forest, color: C.cream }}>
                            Show me <ArrowRight className="w-3.5 h-3.5" style={{ color: C.lime }} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {sections.length === 0 && <p className="text-center text-sm py-8" style={{ color: C.muted }}>No guides match “{q}”.</p>}
            </>
          )}
        </div>

        {!openArticle && (
          <div className="px-6 py-4 border-t" style={{ borderColor: 'rgba(26,67,49,0.1)' }}>
            <button onClick={() => { closeHelp(); start(true); }} data-testid="help-replay-tour"
              className="hg-press w-full flex items-center justify-center gap-2 rounded-full py-3 font-bold" style={{ background: 'rgba(107,159,74,0.14)', color: C.forest }}>
              <Navigation className="w-[18px] h-[18px]" style={{ color: C.leaf }} /> Replay welcome tour
            </button>
          </div>
        )}
      </motion.div>
    </div>
  );
}

// Transient "Show Me" spotlight — pulses on the feature, dismisses on click or after a few seconds.
export function HelpSpotlight({ guide }) {
  const { clearHelpSpot } = useTour();
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let timer; let tries = 0;
    const measure = () => {
      const el = document.querySelector(guide.target);
      if (el) { const r = el.getBoundingClientRect(); if (r.width > 0 && r.height > 0) { setRect({ x: r.left, y: r.top, width: r.width, height: r.height }); return true; } }
      return false;
    };
    const poll = () => { if (measure()) return; tries += 1; if (tries < 26) timer = setTimeout(poll, 140); };
    poll();
    const onMove = () => measure();
    window.addEventListener('resize', onMove); window.addEventListener('scroll', onMove, true);
    const auto = setTimeout(clearHelpSpot, 6000);
    return () => { clearTimeout(timer); clearTimeout(auto); window.removeEventListener('resize', onMove); window.removeEventListener('scroll', onMove, true); };
  }, [guide, clearHelpSpot]);

  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const PAD = 10;
  const hole = rect ? { top: rect.y - PAD, left: rect.x - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 } : null;
  const below = rect ? rect.y + rect.height / 2 < vh / 2 : true;
  const blk = (s) => <div onClick={clearHelpSpot} style={{ position: 'fixed', background: 'rgba(15,45,31,0.55)', ...s }} />;

  return (
    <div className="fixed inset-0" style={{ zIndex: 1100 }} data-testid="help-spotlight">
      {hole ? (
        <>
          {blk({ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) })}
          {blk({ top: hole.top + hole.height, left: 0, right: 0, bottom: 0 })}
          {blk({ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height })}
          {blk({ top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height })}
          <div style={{ position: 'fixed', top: hole.top, left: hole.left, width: hole.width, height: hole.height, borderRadius: 16, border: `2.5px solid ${C.gold}`, boxShadow: `0 0 0 3px ${C.gold}40, 0 0 30px ${C.gold}80`, animation: 'hgPulseRing 1.4s ease-in-out infinite', pointerEvents: 'none' }} />
          <style>{`@keyframes hgPulseRing{0%,100%{opacity:.6}50%{opacity:1}}`}</style>
          <div style={{ position: 'fixed', left: Math.min(Math.max(rect.x + rect.width / 2 - 150, 16), vw - 316), ...(below ? { top: hole.top + hole.height + 14 } : { bottom: vh - hole.top + 14 }) }}>
            <div className="hg-glass rounded-2xl shadow-xl p-4" style={{ maxWidth: 300 }}>
              <p className="font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest, fontSize: 15 }}>{guide.title}</p>
              <p className="mt-1" style={{ color: C.muted, fontSize: 13, lineHeight: 1.45 }}>{guide.desc}</p>
              <button onClick={clearHelpSpot} data-testid="help-spotlight-got-it" className="hg-press mt-3 rounded-full px-4 py-1.5 text-[12px] font-semibold" style={{ background: C.forest, color: C.cream }}>Got it</button>
            </div>
          </div>
        </>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'rgba(15,45,31,0.55)' }} onClick={clearHelpSpot}>
          <div className="hg-glass rounded-2xl shadow-xl p-5 text-center" style={{ maxWidth: 320 }}>
            <p className="font-bold" style={{ color: C.forest, fontSize: 16 }}>{guide.title}</p>
            <p className="mt-1" style={{ color: C.muted, fontSize: 13 }}>{guide.desc}</p>
            <button onClick={clearHelpSpot} data-testid="help-spotlight-got-it" className="hg-press mt-3 rounded-full px-5 py-2 text-[13px] font-semibold" style={{ background: C.forest, color: C.cream }}>Got it</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpCentre;
